
import { supabase } from "@/integrations/supabase/client";
import { FeatureRequest, StatusCounts } from "@/lib/types/feature-management";
import { toast } from "sonner";
import { useAuth } from "@/lib/context/auth-context";

// Fetch all feature requests
export const fetchFeatureRequests = async (): Promise<FeatureRequest[]> => {
  const { data, error } = await supabase
    .from('feature_requests')
    .select('*')
    .order('date_submitted', { ascending: false });

  if (error) {
    console.error('Error fetching feature requests:', error);
    throw error;
  }

  return data as FeatureRequest[];
};

// Fetch a single feature request by ID
export const fetchFeatureRequestById = async (id: string): Promise<FeatureRequest | null> => {
  const { data, error } = await supabase
    .from('feature_requests')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching feature request with ID ${id}:`, error);
    return null;
  }

  return data as FeatureRequest;
};

// Create a new feature request
export const createFeatureRequest = async (request: Omit<FeatureRequest, 'id' | 'created_at' | 'updated_at'>): Promise<FeatureRequest> => {
  // Ensure that submitted_by is set to the current user's ID if not already specified
  if (!request.submitted_by) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      request.submitted_by = session.user.id;
    } else {
      console.error('No authenticated user found when creating feature request');
      toast.error('You must be logged in to submit a feature request');
      throw new Error('Authentication required');
    }
  }

  const { data, error } = await supabase
    .from('feature_requests')
    .insert(request)
    .select()
    .single();

  if (error) {
    console.error('Error creating feature request:', error);
    toast.error('Failed to create feature request');
    throw error;
  }

  toast.success('Feature request created successfully');
  return data as FeatureRequest;
};

// Update an existing feature request
export const updateFeatureRequest = async (id: string, updates: Partial<FeatureRequest>): Promise<FeatureRequest | null> => {
  try {
    // Log the ID being used for debugging
    console.log(`Attempting to update feature request with ID: ${id}`);
    
    // First check if the record exists
    const { data: existingRecord, error: checkError } = await supabase
      .from('feature_requests')
      .select('id')
      .eq('id', id)
      .maybeSingle();

    if (checkError) {
      console.error(`Error checking feature request with ID ${id}:`, checkError);
      toast.error('Error checking if feature request exists');
      return null;
    }

    if (!existingRecord) {
      console.error(`Feature request with ID ${id} not found`);
      toast.error('Feature request not found');
      return null;
    }

    console.log(`Found existing record:`, existingRecord);

    // Now proceed with the update
    const { data, error } = await supabase
      .from('feature_requests')
      .update({ 
        ...updates, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      console.error(`Error updating feature request with ID ${id}:`, error);
      toast.error('Failed to update feature request');
      return null;
    }

    toast.success('Feature request updated successfully');
    return data as FeatureRequest;
  } catch (error) {
    console.error(`Error in updateFeatureRequest:`, error);
    toast.error('Failed to update feature request');
    return null;
  }
};

// Delete a feature request
export const deleteFeatureRequest = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('feature_requests')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting feature request with ID ${id}:`, error);
    toast.error('Failed to delete feature request');
    throw error;
  }

  toast.success('Feature request deleted successfully');
};

// Get status counts for dashboard stats
export const getFeatureRequestStatusCounts = async (): Promise<StatusCounts> => {
  const { data, error } = await supabase
    .from('feature_requests')
    .select('status, priority');

  if (error) {
    console.error('Error fetching feature request status counts:', error);
    return {
      pending: 0,
      inProgress: 0,
      completed: 0,
      highPriority: 0
    };
  }

  const counts: StatusCounts = {
    pending: data.filter(item => item.status === 'pending').length,
    inProgress: data.filter(item => item.status === 'in-progress').length,
    completed: data.filter(item => item.status === 'completed').length,
    highPriority: data.filter(item => item.priority === 'high').length
  };

  return counts;
};

// Custom hook for creating a feature request
export const useCreateFeatureRequest = () => {
  const { user, profile } = useAuth();

  const submit = async (formData: {
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    department: string;
  }) => {
    try {
      if (!user) {
        toast.error('You must be logged in to submit a feature request');
        return null;
      }

      const newRequest = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        department: formData.department,
        submitted_by: user.id,
        submitted_by_name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Anonymous User',
        status: 'pending' as const,
        assigned_to: 'Unassigned',
        date_submitted: new Date().toISOString()
      };

      return await createFeatureRequest(newRequest);
    } catch (error) {
      console.error('Error creating feature request:', error);
      return null;
    }
  };

  return { submit };
};
